import type { Command } from 'commander';
import { asPaginatedBody } from '@wport/core';
import { enterpriseGet } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { dim, printJson, printTable, type OutputFormat } from '../../../lib/output';
import { parseFieldsList, pickPaths } from '../../../lib/path-utils';

interface ListFlags {
	page?: number;
	pageSize?: number;
	keyword?: string;
	status?: string;
	fields?: string;
	minimal?: boolean;
}

/**
 * Server 契約（EnterpriseJobsQueryDto.status: number，0=未刊登,1=已刊登）。
 * openapi.yaml 寫的 active|inactive|deleted 是 drift，送字串會 400 —— 一律走這個映射。
 */
const STATUS_MAP: Record<string, number> = { published: 1, unpublished: 0 };

const MINIMAL_LIST_FIELDS = ['enc_id', 'job_title', 'status', 'updated_at'];

/** 欄位對齊 server EnterpriseJobVm（enterprise-job.vm.ts）。 */
interface EnterpriseJobItem {
	enc_id?: string;
	job_title?: string | null;
	code?: string | null;
	status?: number;
	created_at?: string | null;
	updated_at?: string | null;
	// pm_41 list stats：published_at / clicks_7d 已有值；其餘為 null 佔位（後端後續補值，契約形狀不變）。
	published_at?: string | null;
	clicks_7d?: number | null;
	applications?: number | null;
	visits_7d?: number | null;
	publisher_display?: string | null;
	[k: string]: unknown;
}

function mapStatusFlag(raw: string | undefined): number | undefined {
	if (raw === undefined) return undefined;
	if (raw in STATUS_MAP) return STATUS_MAP[raw];
	throw new CliError(
		`Invalid --status "${raw}". Allowed: ${Object.keys(STATUS_MAP).join(', ')}`,
		ExitCode.InvalidArgument
	);
}

export function formatStatus(status: number | undefined): string {
	if (status === 1) return 'published';
	if (status === 0) return 'unpublished';
	return status === undefined ? '' : String(status);
}

function formatDate(value: string | null | undefined): string {
	return value ? String(value).slice(0, 10) : '';
}

/** 數值型統計欄：0 照印，null/未定義（尚未接的佔位欄）以 — 呈現。 */
function formatCount(value: number | null | undefined): string {
	return typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';
}

/**
 * `enterprise jobs list` 核心：驗 key → GET /jobs → 輸出分頁職缺列表（含 pm_41 list stats）。
 * 與 commander 註冊分離便於測試（mock enterpriseGet 即可 smoke）。
 */
export async function runEnterpriseJobsList(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat; color: boolean },
	apiKey: string,
	flags: ListFlags
): Promise<void> {
	if (flags.fields && flags.minimal) {
		throw new CliError('Use either --fields or --minimal, not both', ExitCode.InvalidArgument);
	}
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/jobs',
		{
			currentPage: flags.page,
			pageSize: flags.pageSize,
			keyword: flags.keyword,
			status: mapStatusFlag(flags.status),
		}
	);
	const paged = asPaginatedBody<EnterpriseJobItem>(body);

	const projection = flags.minimal ? MINIMAL_LIST_FIELDS : flags.fields ? parseFieldsList(flags.fields) : undefined;
	if (projection || ctx.format === 'json') {
		printJson(projection ? { ...paged, data: paged.data.map((row) => pickPaths(row, projection)) } : paged);
		return;
	}

	printTable(
		paged.data,
		[
			{ header: 'ENC_ID', value: (r) => (r.enc_id ?? '').slice(0, 14) },
			{ header: 'TITLE', value: (r) => r.job_title ?? '', maxWidth: 36 },
			{ header: 'STATUS', value: (r) => formatStatus(r.status) },
			{ header: 'CLICKS_7D', value: (r) => formatCount(r.clicks_7d) },
			{ header: 'PUBLISHED', value: (r) => formatDate(r.published_at), maxWidth: 12 },
			{ header: 'UPDATED', value: (r) => formatDate(r.updated_at), maxWidth: 12 },
		],
		ctx.color
	);
	const head = `Showing page ${paged.currentPage}/${paged.totalPages} (${paged.data.length} of ${paged.totalCount} jobs).`;
	const hint =
		paged.totalPages > paged.currentPage ? `  Next: wport enterprise jobs list --page ${paged.currentPage + 1}` : '';
	process.stdout.write(dim(head + hint, ctx.color) + '\n');
}

export function registerEnterpriseJobsList(parent: Command): void {
	parent
		.command('list')
		.description('List your company job postings')
		.option('--page <n>', 'page number (server: currentPage, default 1)', (v) => Number(v))
		.option('--page-size <n>', 'items per page (server: pageSize, default 10, max 100)', (v) => Number(v))
		.option('--keyword <kw>', 'filter by job title keyword')
		.option('--status <state>', 'filter by status: published | unpublished')
		.option('--fields <list>', 'output selected fields as JSON (comma-separated dotted paths)')
		.option('--minimal', `output only ${MINIMAL_LIST_FIELDS.join(',')} as JSON`)
		.action(async (flags: ListFlags, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runEnterpriseJobsList(ctx, key, flags);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { mapStatusFlag, formatStatus, formatCount };
